// The headless editing model. No DOM. The 2D editor and, later, the 3D view (raycasting onto the pumpkin and
// handing over sheet coordinates) both drive it through the same pointer API.

import type { Carve, CarveTool, Part, Stencil, Stroke } from '../types';
import { coverage, makeCanvas, mirrored, reflectStroke } from './raster';
import { carveRaster, carvedAt, SPAN_X } from './carve';
import { inRegion, traceField, type Region } from './regions';
import {
  bounds, circlePoly, dist, distToPolyline, distToSegment, findLoop, partWorldPolys, pathLength, pointInPoly,
  pointInPolys, projectOnSegment, simplifyStroke, type Pt,
} from './geom';
import { PARTS } from './parts';

export type Tool = CarveTool | 'move' | 'points' | 'ruler';
export interface CarveOpts { tool: Carve['tool']; depth: number; width: number; clip?: boolean }
export type ChangeReason = 'stencil' | 'selection' | 'tool' | 'options' | 'draft' | 'hover';
export type HandleName = 'nw' | 'ne' | 'sw' | 'se' | 'rotate';
type Box = { minX: number; minY: number; maxX: number; maxY: number };

/** a turned box's own axes (unit, along the top and up the side) and the middle of the edge the turn handle hangs from */
export function handleFrame(h: Record<HandleName, Pt>): { ux: Pt; uy: Pt; stalk: Pt } {
  const unit = (a: Pt, b: Pt, d: Pt): Pt => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]); return l > 1e-6 ? [(b[0] - a[0]) / l, (b[1] - a[1]) / l] : d; };
  const top: Pt = [(h.nw[0] + h.ne[0]) / 2, (h.nw[1] + h.ne[1]) / 2], foot: Pt = [(h.sw[0] + h.se[0]) / 2, (h.sw[1] + h.se[1]) / 2];
  return { ux: unit(h.nw, h.ne, [1, 0]), uy: unit(h.sw, h.nw, [0, 1]), stalk: dist(top, h.rotate) <= dist(foot, h.rotate) ? top : foot };
}
export type Selection = { kind: 'part' | 'stroke' | 'carve'; id: string } | null;
export interface Options { width: number; mirror: boolean; snap: boolean; loops: boolean }
export interface PointerExtras { pressure?: number; alt?: boolean }
export interface Xform { dx?: number; dy?: number; scale?: number; rotate?: number }

export const GRID = 0.05;
const HISTORY_CAP = 100;
const POINT_CAP = 60;
const CLOSE_DIST = 0.08;
const MIN_STEP = 0.004;
/** a knife or fill press that travels less than this is a click: it takes the whole space under it */
const CLICK = 0.012;
const MIN_POINT = 0.015;
const RULER_CLOSE = 0.05;

type Gesture =
  | { type: 'draw'; tool: 'saw' | 'scraper' | 'eraser' | 'pencil'; pts: Pt[]; pressure: number[] }
  | { type: 'poly'; pts: Pt[]; hover: Pt }
  | { type: 'poker'; center: Pt; radius: number }
  | { type: 'xform'; before: string; mode: 'move' | 'scale' | 'rotate'; orig: Part | Stroke | Carve; kind: 'part' | 'stroke' | 'carve'; center: Pt; start: Pt; carve: Carve[]; box: Box | null; angle: number }
  | { type: 'point'; before: string; id: string; index: number };

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

const GROUP_PLACE: Record<string, { scale: number; y: number }> = {
  eyes: { scale: 0.15, y: 0.2 },
  noses: { scale: 0.09, y: -0.05 },
  mouths: { scale: 0.34, y: -0.36 },
  brows: { scale: 0.15, y: 0.42 },
  extras: { scale: 0.25, y: 0 },
};

export function validateStencil(raw: unknown): string[] {
  const errs: string[] = [];
  const st = raw as Stencil;
  const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  if (!st || typeof st !== 'object') return ['not an object'];
  if (!Array.isArray(st.strokes)) errs.push('strokes is not an array');
  if (!Array.isArray(st.parts)) errs.push('parts is not an array');
  if (errs.length) return errs;
  const ids = new Set<string>();
  for (const p of st.parts) {
    if (typeof p.id !== 'string' || ids.has(p.id)) errs.push(`part id ${p.id}`);
    ids.add(p.id);
    if (!PARTS[p.shape]) errs.push(`part ${p.id}: unknown shape ${p.shape}`);
    if (p.tool !== 'saw' && p.tool !== 'scraper') errs.push(`part ${p.id}: tool ${p.tool}`);
    if (![p.x, p.y, p.scale, p.rotate].every(num)) errs.push(`part ${p.id}: numbers`);
    if (typeof p.flip !== 'boolean') errs.push(`part ${p.id}: flip`);
  }
  for (const s of st.strokes) {
    if (typeof s.id !== 'string' || ids.has(s.id)) errs.push(`stroke id ${s.id}`);
    ids.add(s.id);
    if (!['saw', 'scraper', 'poker', 'eraser', 'pencil'].includes(s.tool)) errs.push(`stroke ${s.id}: tool ${s.tool}`);
    if (!num(s.width) || s.width <= 0) errs.push(`stroke ${s.id}: width`);
    if (typeof s.closed !== 'boolean') errs.push(`stroke ${s.id}: closed`);
    if (!Array.isArray(s.points) || !s.points.length || !s.points.every(q => Array.isArray(q) && num(q[0]) && num(q[1]))) errs.push(`stroke ${s.id}: points`);
  }
  return errs;
}

export function normaliseStencil(raw: unknown): Stencil {
  const errs = validateStencil(raw);
  if (errs.length) throw new Error('Not a stencil: ' + errs.slice(0, 3).join('; '));
  const st = raw as Stencil;
  const carve: Carve[] = Array.isArray(st.carve) ? st.carve.filter(c => c && Array.isArray(c.points) && c.points.length && ['knife', 'scraper', 'fill', 'poker'].includes(c.tool)
    && Number.isFinite(c.width) && Number.isFinite(c.depth)).map(c => {
    const old = c as { tool: string };
    const out: Carve = { ...c, tool: old.tool === 'poker' ? 'knife' : c.tool };
    if (old.tool === 'poker') out.depth = 1;
    if (c.clip) out.clip = true; else delete out.clip;
    if (c.flood) out.flood = true; else delete out.flood;
    return out;
  }) : [];
  return clone({ name: st.name, strokes: st.strokes, parts: st.parts, mirror: !!st.mirror, cut: st.cut !== false, carve });
}

export class StencilModel {
  stencil: Stencil;
  tool: Tool = 'saw';
  options: Options = { width: 0.06, mirror: false, snap: false, loops: true };
  /** the hand carving in progress on the pumpkin */
  carveDraft: Carve | null = null;
  /** picked by its reflection: moves go the other way */
  private viaTwin = false;
  selection: Selection = null;
  hover: Pt | null = null;
  hitRadius = 0.04;
  /** how far from a handle a press still takes it, in sheet units; set by whoever knows how big the sheet is on screen */
  handleRadius = 0;
  draft: Stroke[] = [];
  /** white ink is paper: the pen, the ruler and the shapes cut back into black, for cutouts */
  ink: 'black' | 'white' = 'black';

  private past: string[] = [];
  private future: string[] = [];
  private listeners = new Set<(r: ChangeReason) => void>();
  private counter = 0;
  private gesture: Gesture | null = null;
  private draftPoints: Pt[] = [];

  constructor(stencil?: Stencil) {
    this.stencil = stencil ? normaliseStencil(stencil) : { strokes: [], parts: [], mirror: true, cut: false, carve: [] };
    this.reseed();
  }

  onChange(cb: (reason: ChangeReason) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(r: ChangeReason) { for (const cb of [...this.listeners]) cb(r); }

  private reseed() {
    const nums = [...this.stencil.parts, ...this.stencil.strokes, ...(this.stencil.carve ?? [])].map(e => parseInt(e.id.replace(/\D/g, ''), 10) || 0);
    this.counter = Math.max(this.counter, ...nums, 0);
  }

  private newId(prefix: string) { return prefix + ++this.counter; }

  toJSON(): Stencil { return clone(this.stencil); }

  // ---- history

  private push(before: string) {
    this.past.push(before);
    if (this.past.length > HISTORY_CAP) this.past.shift();
    this.future = [];
  }

  private act(fn: () => void) {
    const before = JSON.stringify(this.stencil);
    fn();
    if (JSON.stringify(this.stencil) !== before) { this.push(before); this.emit('stencil'); }
  }

  private commit(before: string) {
    if (JSON.stringify(this.stencil) !== before) this.push(before);
  }

  get canUndo() { return this.past.length > 0; }
  get canRedo() { return this.future.length > 0; }

  private restore(json: string) {
    this.stencil = JSON.parse(json);
    if (this.selection && !this.find(this.selection)) this.selection = null;
    this.emit('stencil');
    this.emit('selection');
  }

  undo() {
    this.cancel();
    const prev = this.past.pop();
    if (prev === undefined) return;
    this.future.push(JSON.stringify(this.stencil));
    this.restore(prev);
  }

  redo() {
    this.cancel();
    const next = this.future.pop();
    if (next === undefined) return;
    this.past.push(JSON.stringify(this.stencil));
    this.restore(next);
  }

  loadStencil(st: unknown) {
    const clean = normaliseStencil(st);
    this.act(() => { this.stencil = clean; this.selection = null; });
    this.reseed();
    this.emit('selection');
  }

  clear() {
    this.act(() => { this.stencil = { ...this.stencil, strokes: [], parts: [], carve: [] }; this.selection = null; });
    this.emit('selection');
  }

  // ---- tool and options

  setTool(tool: Tool) {
    if (this.gesture?.type === 'poly') this.rulerFinish();
    this.cancel();
    this.tool = tool;
    if (tool !== 'move' && tool !== 'points' && this.selection) { this.selection = null; this.emit('selection'); }
    this.emit('tool');
  }

  setOptions(o: Partial<Options>) {
    Object.assign(this.options, o);
    this.emit('options');
  }

  private snap(p: Pt): Pt {
    return this.options.snap ? [Math.round(p[0] / GRID) * GRID, Math.round(p[1] / GRID) * GRID] : p;
  }

  // ---- selection

  find(sel: NonNullable<Selection>): Part | Stroke | Carve | undefined {
    if (sel.kind === 'carve') return this.stencil.carve?.find(c => c.id === sel.id);
    return sel.kind === 'part' ? this.stencil.parts.find(p => p.id === sel.id) : this.stencil.strokes.find(s => s.id === sel.id);
  }

  get selected(): Part | Stroke | Carve | undefined { return this.selection ? this.find(this.selection) : undefined; }

  select(sel: Selection) {
    this.selection = sel;
    this.emit('selection');
  }

  hitTest(x: number, y: number): Selection {
    const hit = this.hitDrawn(x, y);
    this.viaTwin = !!hit?.id.endsWith('~');
    if (!hit && this.tool === 'move') return this.hitCarve(x, y);
    return hit && { kind: hit.kind, id: hit.id.replace(/~$/, '') };
  }

  /** a cut made by hand on the pumpkin, the last one on top */
  private hitCarve(x: number, y: number): Selection {
    const p: Pt = [x, y], list = (this.stencil.carve ?? []).filter(c => c.tool !== 'fill');
    for (let i = list.length - 1; i >= 0; i--) {
      const c = list[i], pts = c.points as Pt[];
      if ((c.closed && pointInPoly(p, pts)) || distToPolyline(p, pts, c.closed) <= Math.max(this.hitRadius * 0.6, c.width / 2)) return { kind: 'carve', id: c.id };
    }
    return null;
  }

  /**
   * Move works on solid shapes: the ink traced into one filled outline per piece that holds together, so overlapping
   * strokes and parts move as one. Not a step in the history: it changes how the stencil is held, not how it looks.
   * `flat`, for carving, folds the mirror in. Carving done by hand follows whichever piece it lies on.
   */
  prepareMove(x: number, y: number, flat = false) {
    // the piece in hand stays its own until it is let go, so it can be dragged over another without joining it
    if (this.onHeld(x, y)) return;
    const st = this.stencil;
    if (typeof OffscreenCanvas === 'undefined' && typeof document === 'undefined') return;
    const ink = st.strokes.filter(s => s.tool !== 'pencil');
    let loose = st.parts.length > 0 || ink.some(s => !s.id.startsWith('r') || s.tool === 'eraser') || (flat && !!st.mirror);
    if (!loose) {
      const drawn = mirrored(st).strokes.filter(s => s.tool !== 'pencil').map(s => ({ s, b: bounds([s.points as Pt[]]) }));
      loose = drawn.some((a, i) => drawn.some((c, j) => j > i && a.s.tool === c.s.tool && a.s.id.replace(/~$/, '') !== c.s.id.replace(/~$/, '')
        && a.b.minX <= c.b.maxX && c.b.minX <= a.b.maxX && a.b.minY <= c.b.maxY && c.b.minY <= a.b.maxY));
    }
    if (loose) this.bake(flat);
    if (flat) this.reanchor();
  }

  private bake(flat: boolean) {
    const st = this.stencil, N = 512, cv = makeCanvas(N), EPS = 0.006;
    const mirror = !!st.mirror && !flat;
    const out: Stroke[] = [];
    const run = (real: boolean | undefined, tool: 'saw' | 'scraper') => {
      const mine = (t: string) => (tool === 'scraper' ? t === 'scraper' : t !== 'scraper' && t !== 'eraser' && t !== 'pencil');
      const pick = <T extends { real?: boolean }>(v: T) => !mirror || !!v.real === !!real;
      const sub: Stencil = {
        parts: st.parts.filter(p => mine(p.tool) && pick(p)),
        strokes: st.strokes.filter(s => (mine(s.tool) || s.tool === 'eraser') && pick(s)),
        mirror: mirror ? !real : !!st.mirror, cut: false,
      };
      if (!sub.parts.length && !sub.strokes.some(s => mine(s.tool))) return;
      coverage(cv, sub, N);
      const ctx = cv.getContext('2d') as CanvasRenderingContext2D;
      // the mirror's half: only the left is kept, the right is its reflection again
      if (mirror && !real) ctx.clearRect(Math.ceil(N / 2 + (EPS * N) / 2), 0, N, N);
      const px = ctx.getImageData(0, 0, N, N).data, f = new Float32Array(N * N);
      for (let i = 0; i < f.length; i++) f[i] = px[i * 4 + 3] / 255;
      for (const r of traceField(f, N)) {
        out.push({ id: this.newId('r'), tool, width: 0.01, closed: true, filled: true, points: r.outer, ...(r.holes.length ? { holes: r.holes } : {}), ...(mirror && real ? { real: true } : {}) });
      }
    };
    for (const tool of ['scraper', 'saw'] as const) { if (mirror) { run(false, tool); run(true, tool); } else run(undefined, tool); }
    this.stencil.parts = [];
    this.stencil.strokes = [...out, ...st.strokes.filter(s => s.tool === 'pencil')];
    if (flat) this.stencil.mirror = false;
    this.selection = null;
    this.emit('stencil');
  }

  /** carving by hand belongs to the solid piece most of it lies on */
  private reanchor() {
    const drawn = mirrored(this.stencil).strokes.filter(s => s.closed && s.tool !== 'pencil' && s.tool !== 'eraser');
    for (const c of this.stencil.carve ?? []) {
      const pts = c.points as Pt[];
      let best: string | undefined, most = 0;
      for (const s of drawn) {
        const ring = s.points as Pt[], reach = c.width / 2 + 0.01;
        const n = pts.filter(p => inRegion(p, ring, s.holes as Pt[][] | undefined) || distToPolyline(p, ring, true) <= reach).length;
        if (n > most) { most = n; best = s.id; }
      }
      if (most * 2 >= pts.length) c.on = best; else delete c.on;
    }
  }

  /** whether a press lands on the piece already in hand (or on its reflection): that one keeps hold of it */
  onHeld(x: number, y: number): boolean {
    const sel = this.selection;
    if (!sel) return false;
    if (this.handleAt(x, y)) return true;
    const p: Pt = [x, y], r = this.hitRadius * 0.6;
    if (sel.kind === 'carve') {
      const c = this.find(sel) as Carve | undefined, pts = c?.points as Pt[] | undefined;
      return !!c && !!pts && ((!!c.closed && pointInPoly(p, pts)) || distToPolyline(p, pts, !!c.closed) <= Math.max(r, c.width / 2));
    }
    const drawn = mirrored(this.stencil);
    const list: (Part | Stroke)[] = sel.kind === 'part' ? drawn.parts : drawn.strokes;
    for (const e of list) {
      if (e.id.replace(/~$/, '') !== sel.id) continue;
      let hit: boolean;
      if ('shape' in e) { const polys = partWorldPolys(e); hit = pointInPolys(p, polys) || polys.some(q => distToPolyline(p, q, true) <= r); }
      else {
        const pts = e.points as Pt[];
        hit = e.closed && !e.outline ? inRegion(p, pts, e.holes as Pt[][] | undefined) || distToPolyline(p, pts, true) <= r : distToPolyline(p, pts, false) <= Math.max(r, e.width / 2);
      }
      if (hit) { this.viaTwin = e.id.endsWith('~'); return true; }
    }
    return false;
  }

  /** what is under a point as drawn, reflections included (their ids end in ~) */
  private hitDrawn(x: number, y: number): Selection {
    const p: Pt = [x, y], r = this.hitRadius * 0.6, drawn = mirrored(this.stencil);
    const strokes = drawn.strokes.filter(s => s.tool !== 'pencil');
    for (let i = strokes.length - 1; i >= 0; i--) {
      const s = strokes[i], pts = s.points as Pt[];
      const filled = s.closed && !s.outline;
      if (filled ? inRegion(p, pts, s.holes as Pt[][] | undefined) || distToPolyline(p, pts, true) <= r : distToPolyline(p, pts, false) <= Math.max(r, s.width / 2)) {
        return { kind: 'stroke', id: s.id };
      }
    }
    const parts = drawn.parts;
    for (let i = parts.length - 1; i >= 0; i--) {
      const polys = partWorldPolys(parts[i]);
      if (pointInPolys(p, polys) || polys.some(poly => distToPolyline(p, poly, true) <= r)) return { kind: 'part', id: parts[i].id };
    }
    return null;
  }

  selectionPolys(): Pt[][] {
    const s = this.selected;
    if (!s) return [];
    return 'shape' in s ? partWorldPolys(s) : [s.points as Pt[]];
  }

  selectionBounds() {
    const s = this.selected;
    if (!s) return null;
    const b = bounds(this.selectionPolys());
    const pad = 'shape' in s ? 0.02 : Math.max(0.02, s.width / 2);
    return { minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad };
  }

  /** the box's corners and turn handle; while turning, the box turns with the piece, and squares up on release */
  handles(): Record<HandleName, Pt> | null {
    const g = this.gesture, turning = g?.type === 'xform' && g.mode === 'rotate' && g.box ? g : null;
    const b = turning ? turning.box! : this.selectionBounds();
    if (!b) return null;
    // the turn handle sits above the box, or below it when above would be off the paper
    const off = this.hitRadius * 2.2, up = b.maxY + off < 0.94 || b.minY - off < -0.94;
    const h: Record<HandleName, Pt> = {
      nw: [b.minX, b.maxY], ne: [b.maxX, b.maxY], sw: [b.minX, b.minY], se: [b.maxX, b.minY],
      rotate: [(b.minX + b.maxX) / 2, up ? b.maxY + off : b.minY - off],
    };
    if (!turning) return h;
    const [cx, cy] = turning.center, co = Math.cos(turning.angle), si = Math.sin(turning.angle);
    for (const k of Object.keys(h) as HandleName[]) { const [x, y] = h[k]; h[k] = [cx + (x - cx) * co - (y - cy) * si, cy + (x - cx) * si + (y - cy) * co]; }
    return h;
  }

  /** handles are hit a little wider than they are drawn; the nearest wins */
  handleAt(x: number, y: number): HandleName | null {
    const h = this.handles();
    if (!h) return null;
    let best: HandleName | null = null, bd = Math.max(this.hitRadius * 1.8, this.handleRadius);
    for (const name of Object.keys(h) as HandleName[]) { const d = dist([x, y], h[name]); if (d <= bd) { bd = d; best = name; } }
    return best;
  }

  private selCentre(): Pt {
    const s = this.selected!;
    if ('shape' in s) { const b = bounds(partWorldPolys(s)); return [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2]; }
    const b = bounds([s.points as Pt[]]);
    return [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2];
  }

  private apply(orig: Part | Stroke | Carve, target: Part | Stroke | Carve, c: Pt, x: Xform, carve?: Carve[]) {
    if (this.viaTwin) x = { ...x, dx: -(x.dx ?? 0), rotate: -(x.rotate ?? 0) };
    const map = mapper(c, x);
    // carving done inside this shape goes with it; inside its reflection, with the reflection
    if (carve) {
      const twin = mapper([-c[0], c[1]], { ...x, dx: -(x.dx ?? 0), rotate: -(x.rotate ?? 0) });
      const k = x.scale ?? 1;
      for (const o of carve) {
        const t = this.stencil.carve?.find(q => q.id === o.id);
        if (!t) continue;
        const m = o.on === orig.id ? map : twin;
        t.points = o.points.map(p => m(p[0], p[1]));
        t.width = o.width * k;
      }
    }
    const k = x.scale ?? 1, a = x.rotate ?? 0;
    if ('shape' in orig) {
      const t = target as Part;
      [t.x, t.y] = map(orig.x, orig.y);
      t.scale = orig.scale * k;
      t.rotate = orig.rotate + a;
    } else {
      const t = target as Stroke;
      t.points = orig.points.map(p => map(p[0], p[1]));
      if ('holes' in orig && orig.holes) t.holes = orig.holes.map(h => h.map(p => map(p[0], p[1])));
      t.width = orig.width * k;
    }
  }

  /** with the mirror on a shape dragged over the middle is kept as its reflection, on the left */
  private keepLeft(s: Part | Stroke | Carve) {
    if (!this.stencil.mirror || this.selection?.kind === 'carve' || ('real' in s && s.real)) return;
    const b = bounds('shape' in s ? partWorldPolys(s) : [s.points as Pt[]]);
    if ((b.minX + b.maxX) / 2 <= 0.02) return;
    if ('shape' in s) { s.x = -s.x; s.rotate = -s.rotate; s.flip = !s.flip; }
    else {
      s.points = s.points.map(([x, y]) => [-x, y] as Pt);
      if ('holes' in s && s.holes) s.holes = s.holes.map(h => h.map(([x, y]) => [-x, y] as Pt));
    }
  }

  transformSelection(x: Xform) {
    const s = this.selected;
    if (!s) return;
    this.act(() => { this.apply(clone(s), s, this.selCentre(), x, this.anchored(s.id)); this.keepLeft(s); });
  }

  /** whether a shape has been cut into the pumpkin: all of them once the holes are cut, else any with carving on it */
  isCut(id: string): boolean {
    return !!this.stencil.cut || (this.stencil.carve ?? []).some(c => c.on === id || c.on === id + '~');
  }

  private anchored(id: string): Carve[] {
    const all = this.stencil.carve ?? [], i = all.findIndex(c => c.id === id);
    if (i < 0) return clone(all.filter(c => c.on === id || c.on === id + '~'));
    // a cut made by hand takes along what was carved over it afterwards: a fill, a scrape round its edge
    const me = all[i], line = me.points as Pt[];
    return clone(all.slice(i + 1).filter(c => {
      const reach = me.width / 2 + c.width / 2;
      const near = (c.points as Pt[]).filter(p => (me.closed && pointInPoly(p, line)) || distToPolyline(p, line, me.closed) <= reach).length;
      return near * 2 >= c.points.length;
    }).map(c => ({ ...c, on: id })));
  }

  deleteSelection() {
    const sel = this.selection;
    if (!sel) return;
    this.act(() => {
      if (sel.kind === 'part') this.stencil.parts = this.stencil.parts.filter(p => p.id !== sel.id);
      else if (sel.kind === 'carve') this.stencil.carve = (this.stencil.carve ?? []).filter(c => c.id !== sel.id);
      else this.stencil.strokes = this.stencil.strokes.filter(s => s.id !== sel.id);
      this.selection = null;
    });
    this.emit('selection');
  }

  duplicateSelection() {
    const s = this.selected;
    if (!s || !this.selection) return;
    const kind = this.selection.kind, copy = clone(s);
    copy.id = this.newId(kind === 'part' ? 'p' : 's');
    this.act(() => {
      if ('shape' in copy) { copy.x += 0.06; copy.y -= 0.06; this.stencil.parts.push(copy); }
      else { (copy as Stroke).points = (copy as Stroke).points.map(p => [p[0] + 0.06, p[1] - 0.06]); this.stencil.strokes.push(copy as Stroke); }
      this.selection = { kind, id: copy.id };
    });
    this.emit('selection');
  }

  bringForward(by = 1) {
    const sel = this.selection;
    if (!sel) return;
    this.act(() => {
      const arr: { id: string }[] = sel.kind === 'part' ? this.stencil.parts : this.stencil.strokes;
      const i = arr.findIndex(e => e.id === sel.id), j = Math.max(0, Math.min(arr.length - 1, i + by));
      if (i >= 0 && i !== j) { const [e] = arr.splice(i, 1); arr.splice(j, 0, e); }
    });
  }

  // ---- parts

  addPart(shape: string, tool: 'saw' | 'scraper' = this.tool === 'scraper' ? 'scraper' : 'saw'): string[] {
    const def = PARTS[shape];
    if (!def) throw new Error('unknown part ' + shape);
    const place = GROUP_PLACE[def.group] ?? GROUP_PLACE.extras;
    const pair = this.options.mirror && (def.group === 'eyes' || def.group === 'brows');
    const xs = pair ? [-0.26, 0.26] : [0];
    const added: string[] = [];
    this.act(() => {
      let nudge = 0;
      while (this.stencil.parts.some(p => p.shape === shape && Math.abs(p.x - (xs[0] + nudge)) < 0.01 && Math.abs(p.y - (place.y - nudge)) < 0.01)) nudge += 0.05;
      xs.forEach((x, i) => {
        const part: Part = {
          id: this.newId('p'), shape, tool, x: x + (pair ? (i ? nudge : -nudge) : nudge), y: place.y - nudge,
          scale: place.scale, rotate: 0, flip: pair && i === 1,
        };
        this.stencil.parts.push(part);
        added.push(part.id);
      });
      this.selection = { kind: 'part', id: added[added.length - 1] };
    });
    this.emit('selection');
    return added;
  }

  // ---- pointer

  down(x: number, y: number, o: PointerExtras = {}) {
    const raw: Pt = [x, y], t = this.tool;
    if (t === 'ruler') { this.hover = raw; this.rulerPoint(raw); return; }
    if (this.gesture) this.cancel();
    this.hover = raw;
    if (t === 'saw' || t === 'scraper' || t === 'eraser' || t === 'pencil') {
      const p = t === 'eraser' ? raw : this.snap(raw);
      this.gesture = { type: 'draw', tool: t, pts: [p], pressure: [o.pressure ?? 0.5] };
      this.refreshDraft();
    } else if (t === 'poker') {
      this.gesture = { type: 'poker', center: this.snap(raw), radius: 0 };
      this.refreshDraft();
    } else if (t === 'move') {
      const h = this.selection ? this.handleAt(x, y) : null;
      if (h) this.startXform(h === 'rotate' ? 'rotate' : 'scale', raw);
      else {
        const hit = this.onHeld(x, y) ? this.selection : this.hitTest(x, y);
        if (hit?.id !== this.selection?.id) this.select(hit);
        if (hit) this.startXform('move', raw);
      }
    } else if (t === 'points') this.downPoints(raw, !!o.alt);
  }

  private startXform(mode: 'move' | 'scale' | 'rotate', start: Pt) {
    const s = this.selected;
    if (!s || !this.selection) return;
    this.gesture = { type: 'xform', before: JSON.stringify(this.stencil), mode, orig: clone(s), kind: this.selection.kind, center: this.selCentre(), start, carve: this.anchored(s.id), box: this.selectionBounds(), angle: 0 };
  }

  private downPoints(p: Pt, alt: boolean) {
    const s = this.selected;
    const idx = s && !('shape' in s) ? (s.points as Pt[]).findIndex(q => dist(q, p) <= this.hitRadius) : -1;
    if (s && !('shape' in s) && idx >= 0) {
      if (alt) {
        const min = s.closed ? 3 : 2;
        if (s.points.length > min) this.act(() => s.points.splice(idx, 1));
      } else this.gesture = { type: 'point', before: JSON.stringify(this.stencil), id: s.id, index: idx };
      return;
    }
    const hit = this.hitTest(p[0], p[1]);
    if (hit?.kind === 'stroke' && hit.id !== this.selection?.id) this.select(hit);
    else if (!hit) this.select(null);
  }

  doubleClick(x: number, y: number) {
    const s = this.selected;
    if (this.tool !== 'points' || !s || 'shape' in s) return;
    const pts = s.points as Pt[], p: Pt = [x, y], n = s.closed ? pts.length : pts.length - 1;
    let best = -1, bd = this.hitRadius;
    for (let i = 0; i < n; i++) {
      const d = distToSegment(p, pts[i], pts[(i + 1) % pts.length]);
      if (d <= bd) { bd = d; best = i; }
    }
    if (best < 0) return;
    this.act(() => {
      const a = pts[best], b = pts[(best + 1) % pts.length];
      s.points.splice(best + 1, 0, projectOnSegment(p, a, b));
    });
  }

  move(x: number, y: number, o: PointerExtras = {}) {
    const g = this.gesture, raw: Pt = [x, y];
    this.hover = raw;
    if (!g) { this.emit('hover'); return; }
    if (g.type === 'poly') {
      g.hover = this.snap(raw);
      this.refreshDraft();
    } else if (g.type === 'draw') {
      const p = g.tool === 'eraser' ? raw : this.snap(raw);
      if (dist(p, g.pts[g.pts.length - 1]) >= MIN_STEP) { g.pts.push(p); g.pressure.push(o.pressure ?? 0.5); this.refreshDraft(); }
    } else if (g.type === 'poker') {
      g.radius = dist(this.snap(raw), g.center);
      this.refreshDraft();
    } else if (g.type === 'xform') {
      const s = this.selected;
      if (!s) return;
      const c = g.center, xf: Xform = {};
      if (g.mode === 'move') {
        const target = this.snap([c[0] + x - g.start[0], c[1] + y - g.start[1]]);
        xf.dx = target[0] - c[0]; xf.dy = target[1] - c[1];
      } else if (g.mode === 'scale') xf.scale = Math.max(0.05, dist(raw, c) / Math.max(1e-6, dist(g.start, c)));
      else xf.rotate = g.angle = Math.atan2(y - c[1], x - c[0]) - Math.atan2(g.start[1] - c[1], g.start[0] - c[0]);
      this.apply(g.orig, s, c, xf, g.carve);
      this.keepLeft(s);
      this.emit('stencil');
    } else if (g.type === 'point') {
      const s = this.stencil.strokes.find(q => q.id === g.id);
      if (s) { s.points[g.index] = this.snap(raw); this.emit('stencil'); }
    }
  }

  up() {
    const g = this.gesture;
    if (!g || g.type === 'poly') return;
    this.gesture = null;
    if (g.type === 'draw') this.finishDraw(g);
    else if (g.type === 'poker') this.finishPoker(g);
    else if (g.type === 'xform' || g.type === 'point') this.commit(g.before);
    this.draft = [];
    this.emit('draft');
  }

  cancel() {
    const g = this.gesture;
    if (!g) return;
    this.gesture = null;
    if (g.type === 'xform' || g.type === 'point') { this.stencil = JSON.parse(g.before); this.emit('stencil'); }
    this.draft = [];
    this.emit('draft');
  }

  get busy() { return this.gesture !== null; }

  // ---- finishing strokes

  private widthFor(g: { pressure: number[] }): number {
    const mean = g.pressure.reduce((a, b) => a + b, 0) / g.pressure.length;
    return +(this.options.width * (mean === 0.5 ? 1 : 0.5 + mean)).toFixed(4);
  }

  private refreshDraft() {
    const g = this.gesture, w = this.options.width;
    if (!g || g.type === 'xform' || g.type === 'point') return;
    let base: Stroke;
    if (g.type === 'poly') {
      this.draft = this.polyDraft(g);
      this.emit('draft');
      return;
    }
    if (g.type === 'poker') {
      base = { id: 'draft', tool: 'poker', width: g.radius * 2, points: circlePoly(g.center[0], g.center[1], Math.max(g.radius, this.defaultHoleRadius()), 1), closed: true };
    } else base = { id: 'draft', tool: g.tool, width: w, points: g.pts, closed: false };
    this.draft = this.options.mirror ? [base, ...this.twins(base, 'draft-twin')] : [base];
    this.emit('draft');
  }

  // ---- the ruler: click 1, 2, 3 for straight segments; a click back on the first point closes and fills the shape

  /** one click of the ruler. Starts a polyline, extends it, or (3+ points, near the first) closes it into a filled shape */
  rulerPoint(raw: Pt) {
    const p = this.snap(raw);
    let g = this.gesture;
    if (g && g.type !== 'poly') { this.cancel(); g = null; }
    if (!g) {
      this.gesture = { type: 'poly', pts: [p], hover: p };
      this.refreshDraft();
      return;
    }
    if (g.type !== 'poly') return;
    if (dist(p, g.pts[g.pts.length - 1]) < MIN_POINT) return;
    if (g.pts.length >= 3 && dist(p, g.pts[0]) < RULER_CLOSE) {
      this.gesture = null;
      this.addStrokes([{ ...this.polyStroke(g.pts, true), id: this.newId('s') }]);
      this.draft = [];
      this.emit('draft');
      return;
    }
    g.pts.push(p);
    g.hover = p;
    this.refreshDraft();
  }

  /** ends a polyline as a straight-line stroke, closed and filled when asked and there are 3 points (double-click); Enter or another tool leave it open. One point or none makes nothing */
  rulerFinish(close = false) {
    const g = this.gesture;
    if (!g || g.type !== 'poly') return;
    this.gesture = null;
    if (g.pts.length >= 2) this.addStrokes([{ ...this.polyStroke(g.pts, close && g.pts.length >= 3), id: this.newId('s') }]);
    this.draft = [];
    this.emit('draft');
  }

  /** true while a polyline is open */
  get ruling() { return this.gesture?.type === 'poly'; }

  private polyStroke(pts: Pt[], closed: boolean): Stroke {
    const r4 = (p: Pt): [number, number] => [+p[0].toFixed(4), +p[1].toFixed(4)];
    return { id: 'draft', tool: this.ink === 'white' ? 'eraser' : 'saw', width: this.options.width, points: pts.map(r4), closed, ...(closed ? { filled: true } : {}) };
  }

  private polyDraft(g: Extract<Gesture, { type: 'poly' }>): Stroke[] {
    const closing = g.pts.length >= 3 && dist(g.hover, g.pts[0]) < RULER_CLOSE;
    if (closing) return [this.polyStroke(g.pts, true)];
    const pts = dist(g.hover, g.pts[g.pts.length - 1]) < MIN_POINT ? g.pts : [...g.pts, g.hover];
    return pts.length >= 2 ? [this.polyStroke(pts, false)] : [];
  }

  // ---- the paper's other tools

  /** the mirror: off, the reflection becomes real ink; on, the right half is the left's reflection again */
  setMirror(on: boolean) {
    if (!!this.stencil.mirror === on) return;
    this.act(() => {
      if (!on) {
        const d = mirrored(this.stencil);
        const ids = new Map<string, string>();
        const fresh = (id: string, k: string) => { if (!id.endsWith('~')) return id; const n = this.newId(k); ids.set(id, n); return n; };
        this.stencil.parts = d.parts.map(p => ({ ...p, id: fresh(p.id, 'p'), real: undefined }));
        this.stencil.strokes = d.strokes.map(s => ({ ...s, id: fresh(s.id, 's'), real: undefined }));
        // carving anchored to a reflection now belongs to the real ink that replaced it
        for (const c of this.stencil.carve ?? []) if (c.on && ids.has(c.on)) c.on = ids.get(c.on);
      }
      else {
        for (const p of this.stencil.parts) p.real = true;
        for (const s of this.stencil.strokes) s.real = true;
      }
      this.stencil.mirror = on;
    });
  }

  /** ink a region solid: a ring found by the fill on the paper */
  addFill(poly: Pt[], select = false) {
    if (poly.length < 3) return;
    const id = this.newId('s');
    this.addStrokes([{ id, tool: this.ink === 'white' ? 'eraser' : 'saw', width: 0.01, closed: true, filled: true, points: poly.map(p => [+p[0].toFixed(4), +p[1].toFixed(4)]) }]);
    if (select) this.select({ kind: 'stroke', id });
  }

  /** solid shapes from outside (an SVG), laid down as they are: with the mirror on they stay real, not reflected */
  addRegions(rs: Region[]) {
    const tool = this.ink === 'white' ? 'eraser' : 'saw', real = !!this.stencil.mirror;
    const r4 = (p: Pt): Pt => [+p[0].toFixed(4), +p[1].toFixed(4)];
    const list: Stroke[] = rs.map(r => ({ id: this.newId('s'), tool, width: 0.01, closed: true, filled: true, points: r.outer.map(r4),
      ...(r.holes.length ? { holes: r.holes.map(h => h.map(r4)) } : {}), ...(real ? { real } : {}) }));
    if (list.length) this.act(() => { this.stencil.strokes.push(...list); });
  }

  /** the stencil's ink cut out of the pumpkin all at once, or put back */
  setCut(on: boolean) {
    this.act(() => { this.stencil.cut = on; });
  }

  // ---- carving by hand on the pumpkin

  carveDown(x: number, y: number, o: CarveOpts) {
    const p: Pt = [x, y], hit = this.hitDrawn(x, y);
    this.carveDraft = {
      id: 'c-draft', tool: o.tool, depth: o.tool === 'knife' ? 1 : o.depth, width: o.width,
      points: [p], closed: false, on: hit?.id, ...(o.clip ? { clip: true } : {}),
    };
    this.emit('draft');
  }

  carveMove(x: number, y: number) {
    const c = this.carveDraft;
    if (!c) return;
    // across the seam at the back the sheet's x jumps a whole turn: keep the stroke going on round
    const lx = c.points[c.points.length - 1][0];
    x += Math.round((lx - x) / (2 * SPAN_X)) * 2 * SPAN_X;
    if (dist([x, y], c.points[c.points.length - 1] as Pt) < MIN_STEP) return;
    c.points.push([x, y]);
    this.emit('draft');
  }

  carveUp() {
    const c = this.carveDraft;
    this.carveDraft = null;
    if (!c) return;
    let pts = c.points as Pt[];
    // a click with the knife cuts out the whole shape under it; with fill, puts a whole hole back
    if ((c.tool === 'knife' || c.tool === 'fill') && pathLength(pts) < CLICK) {
      if (this.spaceAt(pts[0][0], pts[0][1], c.tool === 'knife' ? 'ink' : 'cut')) {
        const whole: Carve = { ...c, id: this.newId('c'), closed: false, flood: true, points: [[+pts[0][0].toFixed(4), +pts[0][1].toFixed(4)]] };
        delete whole.clip;
        this.act(() => { (this.stencil.carve ??= []).push(whole); });
        this.emit('draft');
        return;
      }
    }
    if (pts.length === 1) pts = [pts[0], [pts[0][0] + 0.001, pts[0][1]]];
    const loop = c.tool === 'knife' && pathLength(pts) > 0.2 && dist(pts[0], pts[pts.length - 1]) < CLOSE_DIST;
    pts = simplifyStroke(pts, loop, POINT_CAP);
    const done: Carve = { ...c, id: this.newId('c'), closed: loop, points: pts.map(p => [+p[0].toFixed(4), +p[1].toFixed(4)]) };
    this.act(() => { (this.stencil.carve ??= []).push(done); });
    this.emit('draft');
  }

  /** the carving's key: main sets it to the pumpkin's id, so the renderer and the model share one picture */
  rasterKey = 'model:' + Math.random().toString(36).slice(2);

  /** under a point: the stencil's ink not yet cut through ('ink'), or a hole ('cut'), anywhere on the shell */
  spaceAt(x: number, y: number, kind: 'ink' | 'cut'): boolean {
    return carvedAt(carveRaster(this.rasterKey, this.stencil), x, y, kind);
  }

  /** the stencil with whatever is being drawn or carved right now */
  preview(): Stencil {
    const st = this.stencil;
    return { ...st, strokes: [...st.strokes, ...this.draft], carve: [...(st.carve ?? []), ...(this.carveDraft ? [this.carveDraft] : [])] };
  }

  private defaultHoleRadius() { return Math.max(0.02, this.options.width * 0.8); }

  private twins(s: Stroke, id?: string): Stroke[] {
    const b = bounds([s.points as Pt[]]);
    if (s.tool === 'poker' ? Math.abs((b.minX + b.maxX) / 2) < 0.02 : b.minX < -0.03 && b.maxX > 0.03) return [];
    return [{ ...s, id: id ?? this.newId('s'), points: s.points.map(p => [-p[0], p[1]] as Pt) }];
  }

  private addStrokes(list: Stroke[]) {
    // with the mirror on, ink drawn on the right half is kept as its reflection on the left, where it is drawn from
    if (this.stencil.mirror) list = list.map(s => { const b = bounds([s.points as Pt[]]); return (b.minX + b.maxX) / 2 > 0.02 ? { ...reflectStroke(s), id: s.id } : s; });
    const all = this.options.mirror ? list.flatMap(s => [s, ...this.twins(s)]) : list;
    this.act(() => { this.stencil.strokes.push(...all); });
  }

  private finishPoker(g: Extract<Gesture, { type: 'poker' }>) {
    const r = g.radius > 0.012 ? g.radius : this.defaultHoleRadius();
    const s: Stroke = { id: this.newId('s'), tool: 'poker', width: +(r * 2).toFixed(4), points: circlePoly(g.center[0], g.center[1], r, this.counter), closed: true };
    s.points = s.points.map(p => [+p[0].toFixed(4), +p[1].toFixed(4)]);
    this.addStrokes([s]);
  }

  private finishDraw(g: Extract<Gesture, { type: 'draw' }>) {
    let pts = g.pts.slice();
    const len = pathLength(pts);
    if (g.tool === 'eraser') {
      if (pts.length === 1) pts.push([pts[0][0], pts[0][1]]);
      const open = pts.length > 2 ? simplifyStroke(pts, false, POINT_CAP) : pts;
      this.addStrokes([this.makeStroke('eraser', this.widthFor(g), open, false)]);
      return;
    }
    if (pts.length < 2 || len < 0.03) return;
    let closed = false;
    if (g.tool === 'pencil') { this.addStrokes([this.makeStroke('pencil', this.widthFor(g), simplifyStroke(pts, false, POINT_CAP), false)]); return; }
    // a loop drawn round closes into a shape, unless the paper wants lines left as lines and filled on purpose
    if (this.options.loops && len > 0.2 && dist(pts[0], pts[pts.length - 1]) < CLOSE_DIST) {
      closed = true;
      if (dist(pts[0], pts[pts.length - 1]) < MIN_STEP * 2) pts.pop();
    } else if (this.options.loops) {
      const loop = findLoop(pts);
      if (loop) { pts = loop; closed = true; }
    }
    const b = bounds([pts]);
    if (closed && Math.max(b.maxX - b.minX, b.maxY - b.minY) < 0.03) return;
    pts = simplifyStroke(pts, closed, POINT_CAP);
    this.addStrokes([this.makeStroke(g.tool, this.widthFor(g), pts, closed)]);
  }

  private makeStroke(tool: CarveTool, width: number, pts: Pt[], closed: boolean): Stroke {
    return { id: this.newId('s'), tool, width, closed, points: pts.map(p => [+p[0].toFixed(4), +p[1].toFixed(4)]) };
  }
}

function mapper(c: Pt, x: Xform) {
  const k = x.scale ?? 1, a = x.rotate ?? 0, cos = Math.cos(a), sin = Math.sin(a);
  return (px: number, py: number): Pt => {
    const ox = (px - c[0]) * k, oy = (py - c[1]) * k;
    return [c[0] + ox * cos - oy * sin + (x.dx ?? 0), c[1] + ox * sin + oy * cos + (x.dy ?? 0)];
  };
}
